/* Wide product photo tiles take their ground from the photo itself, so studio photos never show
   a box inside the tile. An even light-grey edge becomes the tile colour; an even pure-white edge
   multiplies into the tile; anything else (lifestyle, full-bleed) is left alone. The photo viewer's
   slides and thumbnails take it too, and a cq:ground event lets the viewer and the Configure window
   spread the current photo's ground over their whole surface. */
(() => {
  const SELECTOR = '.cq-product-page .product-media img, .cq-configure-slide > img, .cq-configure-thumbnails img, .cq-lightbox-slide > img, .cq-lightbox-thumb img';
  const TILE = '.product-media, .cq-configure-slide, .cq-configure-thumbnails button, .cq-lightbox-slide, .cq-lightbox-thumb';
  const EDGE = [0, 8, 15, 128, 143, 240, 255]; /* corners and edge midpoints of a 16 x 16 sample */
  // The same without the top centre, which a tall product (a control head or valve) reaches. Read only when the full
  // edge is uneven, and only for a light-grey ground (no channel near white), so every photo the full edge decides
  // keeps its result.
  const SIDES = EDGE.filter((p) => p !== 8);
  // Ground sampling is cosmetic and runs one small sample per idle period. Safari and every iPhone
  // browser (WebKit) have no requestIdleCallback, and this script used to stop there, so on iPhones a
  // re-shot grey studio photo still listed as pure white kept its multiply blend and showed as a
  // darker box in its tile (AJ 2026-10-17). There a short timer stands in, one photo per tick, and
  // only after the photo has decoded, so the sample never forces a decode or holds up a tap.
  const idle = window.requestIdleCallback
    ? (callback) => window.requestIdleCallback(callback)
    : (callback) => window.setTimeout(() => callback({ timeRemaining: () => 8 }), 32);
  const pending = new Set();
  let ctx;
  let scheduled = false;
  let loaded = document.readyState === 'complete';

  function schedule() {
    if (!loaded || scheduled || !pending.size) return;
    scheduled = true;
    idle((deadline) => {
      scheduled = false;
      // No timeout: work must remain idle-only. Yield after one small sample.
      if (deadline.timeRemaining() >= 8) {
        const img = pending.values().next().value;
        pending.delete(img);
        if (img.isConnected) ground(img);
      }
      schedule();
    });
  }

  function enqueue(img) {
    if (!img.complete || !img.naturalWidth || img.dataset.cqGround) return;
    if (!window.requestIdleCallback && img.decode) {
      img.decode().then(() => { pending.add(img); schedule(); }, () => {});
      return;
    }
    pending.add(img);
    schedule();
  }

  // { lo, hi, rgb } when these points read even (channel spread up to 4), else null.
  function even(data, points) {
    let r = 0, g = 0, b = 0, lo = 255, hi = 0;
    for (const p of points) {
      const k = p * 4;
      r += data[k]; g += data[k + 1]; b += data[k + 2];
      lo = Math.min(lo, data[k], data[k + 1], data[k + 2]);
      hi = Math.max(hi, data[k], data[k + 1], data[k + 2]);
    }
    const n = points.length;
    return hi - lo > 4 ? null : { lo, hi, rgb: `rgb(${Math.round(r / n)} ${Math.round(g / n)} ${Math.round(b / n)})` };
  }

  function ground(img) {
    if (!img.complete || !img.naturalWidth || img.dataset.cqGround) return;
    const tile = img.closest(TILE);
    if (!tile) return;
    if (ctx === undefined) {
      const canvas = document.createElement('canvas');
      canvas.width = canvas.height = 16;
      ctx = canvas.getContext('2d', { willReadFrequently: true });
    }
    if (!ctx) return;
    let data;
    try {
      ctx.clearRect(0, 0, 16, 16);
      ctx.drawImage(img, 0, 0, 16, 16);
      data = ctx.getImageData(0, 0, 16, 16).data;
    } catch (error) {
      return;
    }
    if (EDGE.some((p) => data[p * 4 + 3] < 250)) return;
    img.dataset.cqGround = 'checked';
    let edge = even(data, EDGE);
    if (!edge) {
      edge = even(data, SIDES);
      if (edge && edge.hi >= 252) edge = null;
    }
    if (!edge || edge.lo < 232) return;
    if (edge.lo >= 252) {
      img.style.mixBlendMode = 'multiply';
      return;
    }
    tile.style.setProperty('--cq-tile', edge.rgb);
    img.style.mixBlendMode = 'normal';
    // The photo viewer and the Configure window carry the current photo's ground across their whole surface.
    tile.dispatchEvent(new CustomEvent('cq:ground', { bubbles: true }));
  }

  document.addEventListener('load', (event) => {
    if (event.target instanceof HTMLImageElement && event.target.matches(SELECTOR)) enqueue(event.target);
  }, true);

  const sweep = () => {
    loaded = true;
    document.querySelectorAll(SELECTOR).forEach(enqueue);
    schedule();
  };
  if (!loaded) window.addEventListener('load', sweep, { once: true });
  else sweep();
})();
